/**
 * useOpenTabs — localStorage-backed open tab management
 *
 * Tracks which session tabs are open and which is active.
 * Persists across page reloads via localStorage.
 *
 * Pure logic lives in openTabsLogic.ts for testability.
 */
import { useState, useCallback, useEffect } from 'react'
import {
  type OpenTabsData,
  loadFromStorage,
  saveToStorage,
  addTab as addTabLogic,
  removeTab as removeTabLogic,
  replaceTab as replaceTabLogic,
  syncWithSessions as syncLogic,
  reorderTabs as reorderLogic,
} from './openTabsLogic.js'

export function useOpenTabs(workspaceId: string, userEmail?: string) {
  const [data, setData] = useState<OpenTabsData>(() => loadFromStorage(workspaceId, userEmail))

  // Re-load from storage when identity changes (account switch)
  useEffect(() => {
    setData(loadFromStorage(workspaceId, userEmail))
  }, [workspaceId, userEmail])

  const addTab = useCallback((id: string) => {
    setData(prev => {
      const next = addTabLogic(prev, id)
      if (!next) return prev
      saveToStorage(workspaceId, next, userEmail)
      return next
    })
  }, [workspaceId, userEmail])

  const removeTab = useCallback((id: string): string | null => {
    let newActive: string | null = null
    setData(prev => {
      const result = removeTabLogic(prev, id)
      if (!result) return prev
      newActive = result.newActive
      saveToStorage(workspaceId, result.data, userEmail)
      return result.data
    })
    return newActive
  }, [workspaceId, userEmail])

  /** #1402 I1: `newId` takes `oldId`'s place (and its active state) — see openTabsLogic `replaceTab`. */
  const replaceTab = useCallback((oldId: string, newId: string) => {
    setData(prev => {
      const next = replaceTabLogic(prev, oldId, newId)
      if (!next) return prev
      saveToStorage(workspaceId, next, userEmail)
      return next
    })
  }, [workspaceId, userEmail])

  const setActiveId = useCallback((id: string | null) => {
    setData(prev => {
      const next = { ...prev, activeId: id }
      saveToStorage(workspaceId, next, userEmail)
      return next
    })
  }, [workspaceId, userEmail])

  /** Move one open tab to another's place. Takes IDS, not indices — the strip
   *  renders a FILTERED view of `tabIds`. */
  const reorderTabs = useCallback((movedId: string, targetId: string) => {
    setData(prev => {
      const next = reorderLogic(prev, movedId, targetId)
      if (!next) return prev
      saveToStorage(workspaceId, next, userEmail)
      return next
    })
  }, [workspaceId, userEmail])

  const syncWithSessions = useCallback((sessionIds: string[]) => {
    setData(prev => {
      const next = syncLogic(prev, sessionIds)
      // Only persist if something changed
      if (
        next.tabIds.length !== prev.tabIds.length ||
        next.tabIds.some((id, i) => id !== prev.tabIds[i]) ||
        next.activeId !== prev.activeId
      ) {
        saveToStorage(workspaceId, next, userEmail)
      }
      return next
    })
  }, [workspaceId, userEmail])

  return {
    tabIds: data.tabIds,
    activeId: data.activeId,
    setActiveId,
    addTab,
    removeTab,
    replaceTab,
    reorderTabs,
    syncWithSessions,
  }
}
